import React from 'react'
import { useSelector } from 'react-redux'
import { InterventionInput, UserGroup } from '../../types'
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Separator } from "../ui/separator"
import { EditIcon } from 'lucide-react'
import { CustomerCodeMappingSection } from './CustomerCodeMappingSection'

interface ViewInterventionInputProps {
  input: InterventionInput | null
  hasEditPermission: boolean
  onEdit: () => void
}

const ViewInterventionInput: React.FC<ViewInterventionInputProps> = ({
  input,
  hasEditPermission,
  onEdit,
}) => {
  const userGroups = useSelector((state: any) => 
    state.auth.groups?.map((group: any) => group?.name)
  ) || []

  const isAdmin = userGroups.some(
    (group: string) => group === UserGroup.ADMIN
  )

  if (!input) return null

  // Determine the specific input type details to render
  const renderTypeSpecificDetails = () => {
    switch (input.input_type) {
      case 'low_carbon_fertilizer':
      case 'conventional_fertilizer':
        const fertInput = input as any
        return (
          <Card className="p-4 mt-4">
            <h3 className="text-lg font-medium mb-4">Fertilizer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailItem label="Fertilizer Type" value={fertInput.fertilizer_type || '-'} />
              <DetailItem label="Nitrogen Content (%)" value={fertInput.nitrogen_content || '-'} />
              <DetailItem label="Crop" value={fertInput.crop || '-'} />
            </div>
          </Card>
        )

      case 'nitrogen_stabilized_fertilizer':
        const stabInput = input as any
        return (
          <Card className="p-4 mt-4">
            <h3 className="text-lg font-medium mb-4">Stabilized Fertilizer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailItem label="Fertilizer Type" value={stabInput.fertilizer_type || '-'} />
              <DetailItem label="Nitrogen Content (%)" value={stabInput.nitrogen_content || '-'} />
              <DetailItem label="Stabilizer Type" value={stabInput.stabilizer_type || '-'} />
              <DetailItem label="Crop" value={stabInput.crop || '-'} />
            </div>
          </Card>
        )

      case 'controlled_release_fertilizer':
        const crfInput = input as any
        return (
          <Card className="p-4 mt-4">
            <h3 className="text-lg font-medium mb-4">Controlled Release Fertilizer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailItem label="Fertilizer Type" value={crfInput.fertilizer_type || '-'} />
              <DetailItem label="Nitrogen Content (%)" value={crfInput.nitrogen_content || '-'} />
              <DetailItem label="CRF Blend Percent (%)" value={crfInput.crf_blend_percent || '-'} />
              <DetailItem label="Crop" value={crfInput.crop || '-'} />
            </div>
          </Card>
        )

      case 'fuel':
        const fuelInput = input as any
        return (
          <Card className="p-4 mt-4">
            <h3 className="text-lg font-medium mb-4">Fuel Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailItem label="Fuel Type" value={fuelInput.fuel_type || '-'} />
            </div>
          </Card>
        )

      default:
        return null
    }
  }

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-xl">{input.input_name}</CardTitle>
        <Badge variant={input.status === 'active' ? 'default' : 'destructive'}>
          {input.status}
        </Badge>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          <section className="space-y-2">
            <h3 className="font-medium text-sm text-muted-foreground">Common Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <DetailItem label="Input Code" value={input.input_code} />
              <DetailItem label="Input ID" value={input._id} />
              <DetailItem label="Input Type" value={input.input_type} />
              <DetailItem label="Emission Factor" value={`${input.emission_factor.toFixed(4)}`} />
              <DetailItem label="Unit" value={input.emission_factor_unit} />
              <DetailItem label="Source" value={input.input_source} />
              <DetailItem label="Year" value={input.year} />
              <DetailItem label="Region" value={input.region} />
              <DetailItem label="Version" value={input.version || '-'} />
            </div>
          </section>

          <Separator />

          <section className="space-y-2">
            <h3 className="font-medium text-sm text-muted-foreground">Type-Specific Details</h3>
            {renderTypeSpecificDetails()}
          </section>

          {hasEditPermission && (
            <>
              <Separator />
              <div className="flex justify-end space-x-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  onClick={onEdit}
                >
                  <EditIcon className="h-4 w-4" />
                  Edit
                </Button>
              </div>
            </>
          )}

          {/* Customer Code Mapping Section - Only for Admin users */}
          {isAdmin && (
            <>
              <Separator />
              <CustomerCodeMappingSection
                inputId={input._id}
                inputName={input.input_name}
                inputType={input.input_type}
                isAdmin={isAdmin}
              />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// Helper component for rendering detail items
const DetailItem: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="mb-4">
    <div className="text-sm font-medium text-gray-500">{label}</div>
    <div className="mt-1">{value}</div>
  </div>
)

export default ViewInterventionInput